import React from 'react';
import { Field, TextInput, SelectInput } from './FormFields';

export const emptyBusinessInfo = {
  businessName: '',
  legalBusinessName: '',
  ein: '',
  website: '',
  yearsInBusiness: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  businessType: '',
  email: '',
  altEmail: '',
  phone: '',
  mobile: '',
  agreeContact: false,
};

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida',
  'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
  'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska',
  'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas',
  'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];

const BUSINESS_TYPES = ['Sole Proprietorship', 'LLC', 'Corporation', 'Partnership', 'Non-Profit'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Formats raw input into a US phone number: (000) 000-0000. Strips non-digits,
// caps at 10, and builds the mask progressively so it works while typing and
// when deleting.
export const formatUsPhone = (input) => {
  const digits = String(input).replace(/\D/g, '').slice(0, 10);
  const len = digits.length;
  if (len === 0) return '';
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isCompleteUsPhone = (val) => String(val).replace(/\D/g, '').length === 10;

// Validates the business step. Returns an { field: message } object; empty means
// valid. Used by OnboardingForm before allowing the user to advance.
export const validateBusinessInfo = (v) => {
  const errors = {};
  const required = {
    businessName: 'Business name is required.',
    legalBusinessName: 'Legal business name is required.',
    addressLine1: 'Address is required.',
    city: 'City is required.',
    state: 'State is required.',
    zip: 'Zip code is required.',
    businessType: 'Business type is required.',
    email: 'Business email is required.',
    phone: 'Business phone is required.',
  };
  Object.entries(required).forEach(([key, msg]) => {
    if (!v[key] || !String(v[key]).trim()) errors[key] = msg;
  });
  if (v.email && !EMAIL_RE.test(v.email)) errors.email = 'Enter a valid email address.';
  if (v.altEmail && !EMAIL_RE.test(v.altEmail)) errors.altEmail = 'Enter a valid email address.';
  if (v.phone && !isCompleteUsPhone(v.phone)) errors.phone = 'Enter a 10-digit US phone number.';
  if (v.mobile && !isCompleteUsPhone(v.mobile)) errors.mobile = 'Enter a 10-digit US phone number.';
  if (!v.agreeContact) errors.agreeContact = 'Please agree to be contacted to continue.';
  return errors;
};

const BusinessInfoStep = ({ value, onChange, errors = {} }) => {
  const set = (key, v) => onChange({ ...value, [key]: v });

  return (
    <div>
      <p className="preob-step__eyebrow">Business Details</p>

      <div className="preob-grid preob-grid--2">
        <Field label="Business Name" required htmlFor="biz-name" error={errors.businessName}>
          <TextInput id="biz-name" value={value.businessName} onChange={(v) => set('businessName', v)} placeholder="e.g. Miami Car Rental" required invalid={!!errors.businessName} />
        </Field>
        <Field label="Legal Business Name" required htmlFor="legal-name" error={errors.legalBusinessName}>
          <TextInput id="legal-name" value={value.legalBusinessName} onChange={(v) => set('legalBusinessName', v)} placeholder="Registered legal name" required invalid={!!errors.legalBusinessName} />
        </Field>

        <Field label="EIN #" htmlFor="ein">
          <TextInput id="ein" value={value.ein} onChange={(v) => set('ein', v)} placeholder="Enter EIN" inputMode="numeric" />
        </Field>
        <Field label="Business Website" htmlFor="website">
          <TextInput id="website" value={value.website} onChange={(v) => set('website', v)} placeholder="www.your-business.com" inputMode="url" />
        </Field>

        <Field label="Years in Business" htmlFor="years" hint="Number of full years operating.">
          <TextInput
            id="years"
            value={value.yearsInBusiness}
            onChange={(v) => set('yearsInBusiness', v.replace(/[^0-9]/g, ''))}
            placeholder="e.g. 3"
            inputMode="numeric"
            maxLength={3}
          />
        </Field>
        <div className="preob-grid__spacer" aria-hidden="true" />

        <Field label="Address Line 1" required htmlFor="addr1" error={errors.addressLine1}>
          <TextInput id="addr1" value={value.addressLine1} onChange={(v) => set('addressLine1', v)} placeholder="Street address" required invalid={!!errors.addressLine1} />
        </Field>
        <Field label="Address Line 2" htmlFor="addr2">
          <TextInput id="addr2" value={value.addressLine2} onChange={(v) => set('addressLine2', v)} placeholder="ex. 125 Park Avenue" />
        </Field>

        <Field label="City" required htmlFor="city" error={errors.city}>
          <TextInput id="city" value={value.city} onChange={(v) => set('city', v)} placeholder="City" required invalid={!!errors.city} />
        </Field>
        <Field label="State" required htmlFor="state" error={errors.state}>
          <SelectInput id="state" value={value.state} onChange={(v) => set('state', v)} required invalid={!!errors.state}>
            <option value="">Select</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </SelectInput>
        </Field>

        <Field label="Zip Code" required htmlFor="zip" error={errors.zip}>
          <TextInput id="zip" value={value.zip} onChange={(v) => set('zip', v)} placeholder="Zip code" inputMode="numeric" maxLength={10} required invalid={!!errors.zip} />
        </Field>
        <Field label="Business Type" required htmlFor="biz-type" error={errors.businessType}>
          <SelectInput id="biz-type" value={value.businessType} onChange={(v) => set('businessType', v)} required invalid={!!errors.businessType}>
            <option value="">Select</option>
            {BUSINESS_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </SelectInput>
        </Field>
      </div>

      <div className="preob-divider" />

      <div className="preob-grid preob-grid--2">
        <Field label="Business Email Address" required htmlFor="email" error={errors.email}>
          <TextInput id="email" type="email" value={value.email} onChange={(v) => set('email', v)} placeholder="name@business.com" inputMode="email" required invalid={!!errors.email} />
        </Field>
        <Field label="Alt Email Address" htmlFor="alt-email" error={errors.altEmail}>
          <TextInput id="alt-email" type="email" value={value.altEmail} onChange={(v) => set('altEmail', v)} placeholder="name@email.com" inputMode="email" invalid={!!errors.altEmail} />
        </Field>

        <Field label="Business Phone Number" required htmlFor="phone" error={errors.phone}>
          <TextInput id="phone" type="tel" value={value.phone} onChange={(v) => set('phone', formatUsPhone(v))} placeholder="(000) 000-0000" inputMode="tel" maxLength={14} required invalid={!!errors.phone} />
        </Field>
        <Field label="Mobile Phone Number" htmlFor="mobile" error={errors.mobile}>
          <TextInput id="mobile" type="tel" value={value.mobile} onChange={(v) => set('mobile', formatUsPhone(v))} placeholder="(000) 000-0000" inputMode="tel" maxLength={14} invalid={!!errors.mobile} />
        </Field>
      </div>

      <label className={`preob-consent${errors.agreeContact ? ' preob-consent--invalid' : ''}`}>
        <input
          type="checkbox"
          checked={value.agreeContact}
          onChange={(e) => set('agreeContact', e.target.checked)}
          className="preob-checkbox"
        />
        <span className="preob-consent__text">
          I agree that Rentid may contact me using the information provided to schedule appointments or for
          marketing purposes.
        </span>
      </label>
      {errors.agreeContact && <p className="preob-field__error preob-consent__error">{errors.agreeContact}</p>}
    </div>
  );
};

export default BusinessInfoStep;
